import {
  ContentService,
  type FlashcardDeckPublic,
  FlashcardDecksService,
  ProgramsService,
} from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { useColorModeValue } from "@/components/ui/color-mode"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import type { IconType } from "react-icons"
import {
  FaBookOpen,
  FaChevronDown,
  FaChevronRight,
  FaTags,
} from "react-icons/fa"

export const Route = createFileRoute("/_layout/flashcards/")({
  component: FlashcardDecksPage,
})

const DECK_GRID_COLUMNS = { base: 1, sm: 2, lg: 3, xl: 4 } as const

function FlashcardDecksPage() {
  const queryClient = useQueryClient()
  const breadcrumbs = useRouteBreadcrumbs("flashcards.index")
  const cardBg = useColorModeValue("white", "gray.900")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const mutedText = useColorModeValue("gray.600", "gray.300")
  const strongText = useColorModeValue("gray.900", "white")
  const deckGroupBg = useColorModeValue("white", "gray.900")
  const deckGroupHeaderBg = useColorModeValue("gray.50", "gray.800")
  const deckCardHoverBorder = useColorModeValue("gray.300", "gray.600")
  const [openDeckGroups, setOpenDeckGroups] = useState<Record<string, boolean>>(
    {},
  )
  const [activeTagsByGroup, setActiveTagsByGroup] = useState<
    Record<string, string | undefined>
  >({})
  const [selectedDeckGroup, setSelectedDeckGroup] = useState<string | null>(
    null,
  )
  const { data, isLoading } = useQuery({
    queryKey: ["flashcard-decks", selectedDeckGroup],
    queryFn: () =>
      selectedDeckGroup === "miscellaneous"
        ? FlashcardDecksService.listDecks({ unassigned: true })
        : FlashcardDecksService.listDecks({ subjectId: selectedDeckGroup! }),
    enabled: selectedDeckGroup !== null,
  })
  const { data: programsData } = useQuery({
    queryKey: ["programs"],
    queryFn: () => ProgramsService.listPrograms({ limit: 200 }),
  })
  const { data: subjectsData } = useQuery({
    queryKey: ["subjects"],
    queryFn: () => ContentService.listSubjects({ limit: 100 }),
  })
  const { data: deckSubjectsData } = useQuery({
    queryKey: ["flashcard-deck-subjects", { includeEmpty: true }],
    queryFn: () =>
      FlashcardDecksService.listDeckSubjects({ includeEmpty: true }),
  })

  const programNameById = new Map(
    (programsData?.data ?? []).map((program) => [program.id, program.name]),
  )
  const subjectNameById = new Map(
    (subjectsData?.data ?? []).map((subject) => [subject.id, subject.name]),
  )
  const availableSubjects = (subjectsData?.data ?? []).filter((subject) =>
    deckSubjectsData?.subject_ids.includes(subject.id),
  )
  const deckGroups = useMemo(
    () => buildDeckGroups(data?.data ?? [], subjectNameById),
    [data?.data, subjectNameById],
  )

  const deleteMutation = useMutation({
    mutationFn: (deckId: string) =>
      FlashcardDecksService.deleteDeck({ deckId }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["flashcard-decks"] }),
  })

  const actions = (
    <HStack>
      <Link to="/flashcards/notes">
        <Button variant="outline">Student Notes</Button>
      </Link>
      <Link to="/flashcards/add">
        <Button>New Deck</Button>
      </Link>
    </HStack>
  )

  return (
    <PageShell
      title="Flashcard Decks"
      description="Create decks, manage cards, and assign study content to programs."
      breadcrumbs={breadcrumbs}
      actions={actions}
    >
      <SectionHeading
        icon={FaBookOpen}
        title="Deck library"
        caption="Open a subject to manage its flashcard decks."
      />

      <VStack gap={5} align="stretch" mb={6}>
        <Flex gap={2} wrap="wrap" role="group" aria-label="Flashcard subjects">
          {availableSubjects.map((subject) => (
            <Button
              key={subject.id}
              variant={selectedDeckGroup === subject.id ? "solid" : "outline"}
              onClick={() => setSelectedDeckGroup(subject.id)}
            >
              {subject.name}
            </Button>
          ))}
          <Button
            variant={
              selectedDeckGroup === "miscellaneous" ? "solid" : "outline"
            }
            onClick={() => setSelectedDeckGroup("miscellaneous")}
          >
            Miscellaneous
          </Button>
        </Flex>

        {selectedDeckGroup === null ? (
          <Text color={mutedText}>Select a subject to load its decks.</Text>
        ) : isLoading ? (
          <PageSkeleton variant="table" rows={4} />
        ) : (
          deckGroups.map((group) => {
            const isOpen = openDeckGroups[group.key] ?? true
            const activeTag = activeTagsByGroup[group.key]
            const visibleDecks = activeTag
              ? group.decks.filter((deck) => deck.tags?.includes(activeTag))
              : group.decks
            const dueCount = group.decks.reduce(
              (total, deck) => total + (deck.due_count ?? 0),
              0,
            )

            return (
              <Box
                key={group.key}
                borderWidth={1}
                borderRadius="lg"
                borderColor={borderColor}
                bg={deckGroupBg}
                overflow="hidden"
              >
                <Button
                  variant="ghost"
                  width="full"
                  h="auto"
                  px={{ base: 4, md: 5 }}
                  py={4}
                  bg={deckGroupHeaderBg}
                  justifyContent="space-between"
                  borderRadius={0}
                  aria-expanded={isOpen}
                  aria-label={`Deck group: ${group.title}`}
                  _hover={{ bg: deckGroupHeaderBg }}
                  onClick={() =>
                    setOpenDeckGroups((current) => ({
                      ...current,
                      [group.key]: !isOpen,
                    }))
                  }
                >
                  <HStack gap={3}>
                    <Icon
                      as={isOpen ? FaChevronDown : FaChevronRight}
                      color={mutedText}
                      boxSize={3}
                    />
                    <Box textAlign="left">
                      <Text fontWeight="bold" color={strongText}>
                        {group.title}
                      </Text>
                      <Text fontSize="xs" color={mutedText}>
                        {group.decks.length}{" "}
                        {group.decks.length === 1 ? "deck" : "decks"}
                      </Text>
                    </Box>
                  </HStack>
                  <Badge colorPalette="teal" variant="subtle">
                    {dueCount} due
                  </Badge>
                </Button>

                {isOpen && (
                  <Box
                    px={{ base: 4, md: 5 }}
                    pt={5}
                    pb={5}
                    borderTopWidth={1}
                    borderColor={borderColor}
                  >
                    {group.tags.length > 0 && (
                      <Flex gap={2} wrap="wrap" mb={4} align="center">
                        <HStack color={mutedText} gap={1} mr={1}>
                          <Icon as={FaTags} boxSize={3} />
                          <Text fontSize="xs" fontWeight="semibold">
                            Tags
                          </Text>
                        </HStack>
                        {group.tags.map((tag) => (
                          <Button
                            key={tag}
                            size="xs"
                            borderRadius="full"
                            variant={activeTag === tag ? "solid" : "outline"}
                            colorPalette={activeTag === tag ? "teal" : "gray"}
                            onClick={() =>
                              setActiveTagsByGroup((current) => ({
                                ...current,
                                [group.key]:
                                  current[group.key] === tag ? undefined : tag,
                              }))
                            }
                          >
                            {tag}
                          </Button>
                        ))}
                      </Flex>
                    )}

                    <SimpleGrid
                      gap={4}
                      justifyContent="start"
                      columns={DECK_GRID_COLUMNS}
                    >
                      {visibleDecks.map((deck) => (
                        <ManagedDeckCard
                          key={deck.id}
                          deck={deck}
                          subjectName={
                            deck.subject_id
                              ? subjectNameById.get(deck.subject_id)
                              : undefined
                          }
                          programNameById={programNameById}
                          borderColor={borderColor}
                          hoverBorderColor={deckCardHoverBorder}
                          bg={cardBg}
                          mutedText={mutedText}
                          strongText={strongText}
                          onDelete={() => deleteMutation.mutate(deck.id)}
                          deleteDisabled={deleteMutation.isPending}
                        />
                      ))}
                    </SimpleGrid>

                    {visibleDecks.length === 0 && (
                      <AppEmptyState
                        variant="inline"
                        title="No decks match this tag."
                        description="Clear the tag filter to see all decks in this group."
                      />
                    )}
                  </Box>
                )}
              </Box>
            )
          })
        )}
        {data?.count === 0 && (
          <AppEmptyState
            title={emptyStateCopy.flashcards.managedNone.title}
            description={emptyStateCopy.flashcards.managedNone.description}
            data-testid="flashcard-decks-empty-state"
          />
        )}
      </VStack>
    </PageShell>
  )
}

export default FlashcardDecksPage

type DeckGroup = {
  key: string
  title: string
  decks: FlashcardDeckPublic[]
  tags: string[]
}

function buildDeckGroups(
  decks: FlashcardDeckPublic[],
  subjectNameById: Map<string, string>,
): DeckGroup[] {
  const groupsByKey = new Map<string, DeckGroup>()

  for (const deck of decks) {
    const hasSubject = Boolean(deck.subject_id)
    const key = hasSubject ? String(deck.subject_id) : "miscellaneous"
    const title = hasSubject
      ? subjectNameById.get(String(deck.subject_id)) ?? "Untitled subject"
      : "Miscellaneous"

    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, { key, title, decks: [], tags: [] })
    }

    groupsByKey.get(key)?.decks.push(deck)
  }

  const groups = Array.from(groupsByKey.values()).map((group) => ({
    ...group,
    decks: [...group.decks].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    ),
    tags: Array.from(
      new Set(group.decks.flatMap((deck) => deck.tags ?? [])),
    ).sort((a, b) => a.localeCompare(b)),
  }))

  return groups.sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
  )
}

function ManagedDeckCard({
  deck,
  subjectName,
  programNameById,
  borderColor,
  hoverBorderColor,
  bg,
  mutedText,
  strongText,
  onDelete,
  deleteDisabled,
}: {
  deck: FlashcardDeckPublic
  subjectName?: string
  programNameById: Map<string, string>
  borderColor: string
  hoverBorderColor: string
  bg: string
  mutedText: string
  strongText: string
  onDelete: () => void
  deleteDisabled: boolean
}) {
  const programLabel =
    (deck.programs?.length ?? 0) > 0
      ? deck
          .programs!.map((programId) => programNameById.get(programId))
          .filter(Boolean)
          .join(", ") || `${deck.programs!.length} program(s)`
      : "All students"

  return (
    <Box
      p={4}
      borderWidth={1}
      borderRadius="lg"
      borderColor={borderColor}
      bg={bg}
      boxShadow="sm"
      minH="236px"
      transition="border-color 0.16s ease, box-shadow 0.16s ease"
      _hover={{ borderColor: hoverBorderColor, boxShadow: "md" }}
    >
      <VStack align="stretch" gap={3} h="100%">
        <Box flex="1">
          <HStack justify="space-between" align="start" gap={3}>
            <Box minW={0}>
              <Text fontWeight="bold" color={strongText} lineClamp={2}>
                {deck.name}
              </Text>
              <Text fontSize="xs" color={mutedText} mt={1}>
                {deck.card_count ?? 0} cards · {programLabel}
              </Text>
            </Box>
            <Badge colorPalette="yellow" variant="subtle" flexShrink={0}>
              {deck.due_count ?? 0} due
            </Badge>
          </HStack>

          <Flex gap={2} wrap="wrap" mt={3}>
            {subjectName && <Badge colorPalette="teal">{subjectName}</Badge>}
            {(deck.tags ?? []).map((tag) => (
              <Badge key={tag} colorPalette="blue" variant="subtle">
                {tag}
              </Badge>
            ))}
          </Flex>
        </Box>

        <Flex
          data-testid={`deck-actions-${deck.id}`}
          gap={2}
          align="stretch"
          w="full"
        >
          <Box flex="1" minW={0}>
            <Link
              to={`/flashcards/${deck.id}/edit`}
              style={{ display: "block", width: "100%", height: "100%" }}
            >
              <Button size="sm" width="full" variant="outline">
                Edit
              </Button>
            </Link>
          </Box>
          <Button
            flex="1"
            minW={0}
            size="sm"
            width="full"
            variant="ghost"
            colorPalette="red"
            onClick={onDelete}
            disabled={deleteDisabled}
          >
            Delete
          </Button>
        </Flex>
      </VStack>
    </Box>
  )
}

function SectionHeading({
  icon,
  title,
  caption,
}: {
  icon: IconType
  title: string
  caption: string
}) {
  return (
    <HStack gap={3} align="center" mb={3}>
      <Box
        boxSize="34px"
        borderRadius="full"
        bg="teal.50"
        color="teal.500"
        display="grid"
        placeItems="center"
        flexShrink={0}
        _dark={{ bg: "teal.950", color: "teal.300" }}
      >
        <Icon as={icon} boxSize={4} />
      </Box>
      <Box>
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="wider"
          color="gray.600"
          _dark={{ color: "gray.300" }}
          fontWeight="bold"
        >
          {title}
        </Text>
        <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
          {caption}
        </Text>
      </Box>
    </HStack>
  )
}
